/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import {
  OperationalDataTransportLocationRecord,
  PreregistrationKeys,
} from './genericElements/operationalData/transportLocation';
import { MissionData, Participants } from '../backendModels/mission-data.model';
import { OperationalDataParticipantsRecord, ParticipantsKeys } from './genericElements/operationalData/participants';
import {
  getMissionLocationNameTuple,
  mapStringToMissionLocationKey,
  MissionLocationKeys,
  OperationalDataLocationRecord,
} from './genericElements/operationalData/missionLocation';
import { ControlCenterRecord } from './genericElements/controlCenter';

export interface NormalizedMission {
  personnelNumber?: string | null;
  location?: string | null;
  controlCenter?: string | null;
  controlCenterLicensePlate?: string | null;

  participants?: Participants | null;

  transportLocation?: string | null;

  missionStreetGPSLatitude?: string | null;
  missionCityGPSLongitude?: string | null;
  missionPostcode?: string | null;
  missionLocationType?: MissionLocationKeys | null;
}

export const operationalPropertiesToLabels: Record<keyof NormalizedMission, string> = {
  personnelNumber: 'Pers. Nr.',
  location: 'Standort',
  controlCenter: 'Leitstelle',
  controlCenterLicensePlate: 'Leitstelle (KFZ-Kennz.)',

  participants: 'Beteiligt',

  transportLocation: 'Name der Einrichtung',

  missionStreetGPSLatitude: 'Straße oder GPS-Breitengrad',
  missionCityGPSLongitude: 'Ort oder GPS-Längengrad',
  missionPostcode: 'PLZ',
  missionLocationType: 'Einsatz-Ort Typ',
};

export function sanitizeMission(missionData?: NormalizedMission): NormalizedMission {
  return {
    personnelNumber: missionData?.personnelNumber ?? null,
    location: missionData?.location ?? null,
    controlCenter: missionData?.controlCenter ?? null,
    controlCenterLicensePlate: missionData?.controlCenterLicensePlate ?? null,

    participants: missionData?.participants ?? null,

    transportLocation: missionData?.transportLocation ?? null,

    missionStreetGPSLatitude: missionData?.missionStreetGPSLatitude ?? null,
    missionCityGPSLongitude: missionData?.missionCityGPSLongitude ?? null,
    missionPostcode: missionData?.missionPostcode ?? null,
    missionLocationType: missionData?.missionLocationType ?? null,
  };
}

export function normalizeMissionData(missionData?: MissionData): NormalizedMission {
  return {
    personnelNumber: missionData?.controlCenter?.personnelNumber ?? null,
    location: missionData?.controlCenter?.location ?? null,
    controlCenter: missionData?.controlCenter?.controlCenter ?? null,
    controlCenterLicensePlate: missionData?.controlCenter?.controlCenterLicensePlate ?? null,

    participants: missionData?.participants ?? null,

    transportLocation: missionData?.transportDestination?.name ?? null,

    missionStreetGPSLatitude: missionData?.missionLocation?.address?.street ?? null,
    missionCityGPSLongitude: missionData?.missionLocation?.address?.city ?? null,
    missionPostcode: missionData?.missionLocation?.address?.postalCode ?? null,
    missionLocationType: mapStringToMissionLocationKey(missionData?.missionLocation?.type) ?? null,
  };
}

type MissionRecords = {
  controlCenterRecord?: ControlCenterRecord;
  operationalParticipantsRecord?: OperationalDataParticipantsRecord;
  operationalLocationRecord?: OperationalDataLocationRecord;
  operationalTransportRecord?: OperationalDataTransportLocationRecord;
};

export function localizeOperationalPropertyTuple(
  propertyName: keyof NormalizedMission,
  propertyValue: ParticipantsKeys | PreregistrationKeys | string,
  records: MissionRecords
): [string, string | null, string | null] {
  const { controlCenterRecord, operationalParticipantsRecord, operationalLocationRecord, operationalTransportRecord } =
    records;

  let oldValue: string | number | PreregistrationKeys[] | ParticipantsKeys[] | null | undefined;

  if (
    propertyName === 'location' ||
    propertyName === 'controlCenter' ||
    propertyName === 'controlCenterLicensePlate' ||
    propertyName === 'personnelNumber'
  ) {
    oldValue = controlCenterRecord?.values?.[propertyName]?.text;
  } else if (propertyName === 'participants') {
    oldValue = operationalParticipantsRecord?.values?.[propertyName]?.options;
  } else if (
    propertyName === 'missionStreetGPSLatitude' ||
    propertyName === 'missionCityGPSLongitude' ||
    propertyName === 'missionPostcode'
  ) {
    oldValue = operationalLocationRecord?.values?.[propertyName]?.text;
  } else if (propertyName === 'missionLocationType') {
    oldValue = operationalLocationRecord?.values?.[propertyName]?.option;
  } else if (propertyName === 'transportLocation') {
    oldValue = operationalTransportRecord?.values?.transportLocation?.text;
  }

  return [
    operationalPropertiesToLabels[propertyName],
    localizePropertyValue(propertyName, propertyValue),
    localizePropertyValue(propertyName, oldValue),
  ];
}

function localizePropertyValue(
  propertyName: string,
  propertyValue: string | number | PreregistrationKeys[] | ParticipantsKeys[] | MissionLocationKeys | null | undefined
): string | null {
  if (propertyValue == null) {
    return null;
  }
  if (propertyName === 'missionLocationType') {
    return getMissionLocationNameTuple(propertyValue as MissionLocationKeys)[1];
  }

  if (propertyName === 'participants' && Array.isArray(propertyValue)) {
    return propertyValue.join(', ');
  }

  return propertyValue?.toString();
}
